import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { API } from '../App';

// Generate or retrieve visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('therentpayer_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('therentpayer_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new for each browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('therentpayer_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('therentpayer_session_id', sessionId);
    sessionStorage.setItem('therentpayer_session_start', Date.now().toString());
    sessionStorage.setItem('therentpayer_pages_visited', '0');
  }
  return sessionId;
};

// Send tracking data
const sendTrackingData = async (endpoint, data) => {
  try {
    await fetch(`${API}/analytics/track/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      keepalive: true // Important for beforeunload
    });
  } catch (e) {
    // Silently fail - analytics should never break the app
  }
};

export function AnalyticsTracker() {
  const location = useLocation();
  const visitorId = useRef(getVisitorId());
  const sessionId = useRef(getSessionId());
  const maxScrollDepth = useRef(0);
  const lastScrollUpdate = useRef(0);

  // Track page view
  useEffect(() => {
    const user = localStorage.getItem('user_token') ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    const agent = localStorage.getItem('agent_token') ? JSON.parse(localStorage.getItem('agent') || '{}') : null;
    
    // Increment pages visited
    const pagesVisited = parseInt(sessionStorage.getItem('therentpayer_pages_visited') || '0') + 1;
    sessionStorage.setItem('therentpayer_pages_visited', pagesVisited.toString());
    
    // Reset scroll depth for new page
    maxScrollDepth.current = 0;

    sendTrackingData('pageview', {
      page_url: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer,
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      user_agent: navigator.userAgent,
      is_authenticated: !!(user || agent),
      user_id: user?.user_id || agent?.agent_id || null,
      timestamp: new Date().toISOString()
    });
  }, [location.pathname]);

  // Track clicks
  const handleClick = useCallback((e) => {
    const target = e.target;
    
    sendTrackingData('click', {
      page_url: window.location.pathname,
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      x: e.clientX,
      y: e.clientY,
      element_tag: target.tagName,
      element_id: target.id || null,
      element_class: target.className?.toString?.() || null,
      element_text: target.textContent?.substring(0, 50) || null,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Track scroll depth
  const handleScroll = useCallback(() => {
    const now = Date.now();
    // Throttle to once per second
    if (now - lastScrollUpdate.current < 1000) return;
    lastScrollUpdate.current = now;

    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight;
    const winHeight = window.innerHeight;
    const scrollPercent = Math.round((scrollTop / (docHeight - winHeight)) * 100) || 0;
    
    if (scrollPercent > maxScrollDepth.current) {
      maxScrollDepth.current = scrollPercent;
      
      // Only send updates at 25% intervals
      if (scrollPercent >= 25 && scrollPercent % 25 < 5) {
        sendTrackingData('scroll', {
          page_url: window.location.pathname,
          session_id: sessionId.current,
          visitor_id: visitorId.current,
          scroll_depth: scrollPercent,
          max_scroll_depth: maxScrollDepth.current,
          page_height: docHeight,
          viewport_height: winHeight,
          timestamp: new Date().toISOString()
        });
      }
    }
  }, []);

  // Track session end
  const handleSessionEnd = useCallback(() => {
    const sessionStart = parseInt(sessionStorage.getItem('therentpayer_session_start') || Date.now().toString());
    const duration = Math.round((Date.now() - sessionStart) / 1000);
    const pagesVisited = parseInt(sessionStorage.getItem('therentpayer_pages_visited') || '1');

    sendTrackingData('session-end', {
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      duration_seconds: duration,
      pages_visited: pagesVisited,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleSessionEnd);

    return () => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleSessionEnd);
    };
  }, [handleClick, handleScroll, handleSessionEnd]);

  return null; // This component doesn't render anything
}

export default AnalyticsTracker;
